// src/pages/auth/OtpVerifyPage.jsx
import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { authApi } from '@/api/services'
import { Button } from '@/components/common/ui'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'

export default function OtpVerifyPage() {
  const [otp, setOtp]         = useState('')
  const [loading, setLoading] = useState(false)
  const { user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Backend exposes `dev_otp` on non-prod / non-stage envs. We seed it from the
  // register-flow navigation state and refresh it from each resend response.
  const [devOtp, setDevOtp] = useState(location.state?.devOtp ?? null)

  const verify = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      await authApi.verifyOtp({ otp })
      toast.success('Phone verified!')
      navigate(user?.role === 'supplier' ? '/supplier' : '/buyer')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP')
    } finally { setLoading(false) }
  }

  const resend = async () => {
    try {
      const { data } = await authApi.resendOtp()
      const next = data?.data?.dev_otp ?? null
      if (next) setDevOtp(next)
      toast.success('OTP resent!')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not resend OTP')
    }
  }

  return (
    <div className="card p-8 text-center">
      <div className="text-4xl mb-4">📱</div>
      <h1 className="font-display font-bold text-2xl mb-2">Verify your phone</h1>
      <p className="text-sm text-muted mb-6">Enter the 6-digit code sent to your registered number</p>

      {devOtp && (
        <div className="mb-6 rounded-xl border border-yellow-300 bg-yellow-50 p-4 text-left">
          <div className="flex items-start gap-2">
            <span className="text-lg leading-none">🛠️</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-900">Dev mode — SMS not sent</p>
              <div className="mt-2 flex items-center gap-2">
                <code
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(devOtp)
                      toast.success('Code copied')
                    } catch { /* ignore */ }
                  }}
                  className="cursor-pointer rounded-md bg-white px-3 py-1.5 font-mono text-lg font-bold tracking-[0.3em] text-yellow-900 border border-yellow-200 hover:bg-yellow-100"
                  title="Click to copy"
                >
                  {devOtp}
                </code>
                <button
                  type="button"
                  onClick={() => setOtp(String(devOtp))}
                  className="text-xs font-semibold text-yellow-900 underline hover:no-underline"
                >
                  Use this code
                </button>
              </div>
              <p className="mt-2 text-[11px] text-yellow-800">
                Stage and production environments still send this code by SMS normally.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={verify} className="space-y-4">
        <input value={otp} onChange={e => setOtp(e.target.value)} maxLength={6}
          placeholder="000000" className="input text-center text-2xl font-bold tracking-[0.5em] py-4" />
        <Button type="submit" loading={loading} className="w-full justify-center py-3">Verify OTP</Button>
      </form>
      <button onClick={resend}
        className="text-sm text-pink hover:underline mt-5">Resend code</button>
    </div>
  )
}
